/**
 * Guía: Reembolsos.
 *
 * Contenido derivado del flujo **F3** del manual —§6 (la vía de soporte: el
 * agente pide, el revisor aprueba), §7 (lo que no se puede hacer)— y no de un
 * capítulo de pantalla, porque **esta pantalla no tiene uno**: apareció en el
 * menú sin entrar en el inventario del panel, y así quedó anotado en el
 * `PENDIENTES.md` del manual.
 *
 * Es la guía con más reglas de las que no se ven, y todas existen por el
 * mismo motivo: aquí se mueve dinero de verdad contra la pasarela. Las dos
 * que más rechazos explican —nadie resuelve la suya, una sola solicitud viva
 * por pago— van con paso propio.
 */

import type { TourFlow } from '../types';

const REEMBOLSOS_PATH = '/refund-requests';

export const f03Reembolsos: TourFlow = {
  id: 'admin.refund-requests',
  chapter: 'F3',
  title: 'Reembolsos',
  summary:
    'El agente pide y el revisor aprueba: por qué son dos personas, qué significa cada estado y qué no se puede devolver.',
  href: REEMBOLSOS_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'Dos escalones, a propósito',
      body:
        'Cuando un caso ya no encaja en las devoluciones automáticas queda el reembolso ' +
        'manual, y tiene dos pasos: un agente lo pide desde su ticket, con el importe y por ' +
        'qué procede, y un revisor lo aprueba. Antes eso ocurría de palabra; ahora queda ' +
        'quién lo pidió, con qué argumento y quién decidió.',
      route: REEMBOLSOS_PATH,
    },
    {
      id: 'who-sees-what',
      title: 'Qué ves depende de tu papel',
      body:
        'Como agente ves las solicitudes que tú creaste. Como revisor —responsable de ' +
        'soporte, finanzas o administrador general— las ves todas, que es lo que permite ' +
        'repartir la cola.',
    },
    {
      id: 'stats',
      title: 'Pendientes, en proceso y aprobadas',
      body:
        'Las tres cifras de arriba son la cola de trabajo. «Pendientes» es lo que espera ' +
        'decisión; es el número que hay que mirar primero.',
      target: 'admin.refunds.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'processing-meaning',
      title: '«En proceso» no es «esperando revisor»',
      body:
        'Significa que un revisor ya la tomó y el reembolso está corriendo contra la ' +
        'pasarela. Es la confusión más cara de esta pantalla: una solicitud en proceso no ' +
        'necesita que nadie la apruebe otra vez. Si lleva ahí más de cinco minutos, algo se ' +
        'cortó a mitad.',
      target: 'admin.refunds.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'reconcile',
      title: 'Una que se quedó a medias se concilia',
      body:
        'Conciliar mira si el reembolso llegó a existir en la pasarela y cierra la solicitud ' +
        'en consecuencia. No adivina: consulta y cierra. Es lo que hay que hacer con una ' +
        'solicitud atascada en proceso, en vez de crear otra.',
      roles: ['SUPER_ADMIN', 'FINANCE', 'SUPPORT_LEAD'],
    },
    {
      id: 'not-your-own',
      title: 'Nadie resuelve la solicitud que él mismo creó',
      body:
        'Ni siendo revisor. El sentido de esta pantalla es que decidan dos personas: quien ' +
        'atiende sabe si procede, y quien aprueba responde del dinero. Si intentas resolver ' +
        'una tuya, el servidor la rechaza — no es un fallo de permisos.',
    },
    {
      id: 'one-per-payment',
      title: 'Una sola solicitud viva por pago',
      body:
        'Si ya hay una en curso sobre ese pago, la nueva se rechaza. Explica casi todos los ' +
        '«no me deja crearla»: antes de insistir, se busca la que ya existe. La que se creó ' +
        'por error la retira quien la creó.',
    },
    {
      id: 'reject-needs-reason',
      title: 'Rechazar exige motivo',
      body:
        'Y no es burocracia: el agente tiene que poder explicárselo al cliente que está ' +
        'esperando. Un rechazo sin motivo deja a quien atiende sin nada que decir.',
      roles: ['SUPER_ADMIN', 'FINANCE', 'SUPPORT_LEAD'],
    },
    {
      id: 'total-closes-order',
      title: 'Aprobar un reembolso total cierra el pedido',
      body:
        'Devuelve el dinero, cancela el pedido, devuelve los Puntos 121 que el cliente ' +
        'canjeó y avisa al comercio y al cliente. Sólo si es total: un parcial —un plato ' +
        'mal, un cobro de más— no tumba el pedido, el cliente se queda con el resto.',
      roles: ['SUPER_ADMIN', 'FINANCE', 'SUPPORT_LEAD'],
    },
    {
      id: 'limits',
      title: 'Lo que no se puede devolver',
      body:
        'Más de lo que se pagó, contando lo ya devuelto. Un pago en efectivo, porque ese ' +
        'dinero nunca entró a la plataforma: lo devuelve el comercio en el local. Y un ' +
        'pedido ya liquidado al comercio, que le quitaría un dinero que ya salió — ese ' +
        'ajuste va por liquidaciones.',
    },
    {
      id: 'where-it-starts',
      title: 'La solicitud nace en el ticket',
      body:
        'No se crea desde aquí: se crea desde el ticket del agente, y el pago tiene que ser ' +
        'del pedido de ese ticket. Esta pantalla es la cola donde se deciden. Si necesitas ' +
        'una y no existe el ticket, empieza por ahí.',
    },
  ],
};
